import {FC, useMemo} from 'react'

const ACCENT = '#039540'
const MUTED = '#7e8299'
const INK = '#1b1b29'

interface PaginationProps {
  /** 1-based current page. */
  page: number
  /** Total number of items across all pages. */
  total: number
  pageSize: number
  onChange: (page: number) => void
}

/** Build a compact page list with ellipses, e.g. [1, '…', 4, 5, 6, '…', 20]. */
function buildPages(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) return Array.from({length: pageCount}, (_, i) => i + 1)
  const pages: (number | '…')[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pageCount - 1, page + 1)
  if (start > 2) pages.push('…')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < pageCount - 1) pages.push('…')
  pages.push(pageCount)
  return pages
}

/**
 * Reusable admin list pagination — "Showing X–Y of N" + page controls.
 * Client-side: the consumer slices its own data by (page, pageSize).
 * Renders nothing when everything fits on one page.
 */
export const Pagination: FC<PaginationProps> = ({page, total, pageSize, onChange}) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const pages = useMemo(() => buildPages(page, pageCount), [page, pageCount])

  if (total === 0 || pageCount <= 1) return null

  const from = (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)
  const go = (p: number) => {
    const next = Math.min(pageCount, Math.max(1, p))
    if (next === page) return
    onChange(next)
    // Jump back to the top of the list on page change (admin scroll area + window).
    document.querySelector('.ccp-admin')?.scrollTo({top: 0, behavior: 'smooth'})
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  return (
    <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 18px'}}>
      <span style={{fontSize: 13, color: MUTED}}>
        Showing <b style={{color: INK}}>{from}</b>–<b style={{color: INK}}>{to}</b> of <b style={{color: INK}}>{total}</b>
      </span>
      <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
        <button onClick={() => go(page - 1)} disabled={page === 1} style={navBtn(page === 1)}>‹</button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} style={{padding: '0 6px', color: MUTED}}>…</span>
          ) : (
            <button key={p} onClick={() => go(p)} style={pageBtn(p === page)}>
              {p}
            </button>
          ),
        )}
        <button onClick={() => go(page + 1)} disabled={page === pageCount} style={navBtn(page === pageCount)}>›</button>
      </div>
    </div>
  )
}

const baseBtn: React.CSSProperties = {
  minWidth: 34,
  height: 34,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 8px',
}

const pageBtn = (active: boolean): React.CSSProperties => ({
  ...baseBtn,
  border: `1px solid ${active ? ACCENT : '#e5e7eb'}`,
  background: active ? ACCENT : '#fff',
  color: active ? '#fff' : INK,
})

const navBtn = (disabled: boolean): React.CSSProperties => ({
  ...baseBtn,
  fontSize: 18,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: disabled ? '#cbd2e0' : INK,
  cursor: disabled ? 'default' : 'pointer',
})
